import {extractMajorVersion} from '../../../utils/versionUtils'

/**
 * Each boundary is the lowest build number that reliably belongs to that release.
 *
 * A pre-release branch continues the buildNumber sequence seeded from the trunk.
 * Next, the trunk receives a buildNumber boost, which is about 6000, but this happens
 * on the second build after the pre-release branch was cut.
 * This 6000 gap leaves the room for the releases build.
 *
 * Some builds of a release exist before the below boundaries, but this only happens
 * for EAP and test users, which are not in the scope of this change.
 *
 * The already released major version, so anything below this is definitely not 2026.1.
 */
export const FIRST_BUILD_NUMBER_2026_1 = 222521

/**
 * The first green build of the 2026.2 pre-release branch:
 * https://teamcity-nightly.labs.intellij.net/buildConfiguration/TeamCity_20262x_BuildDistTarGzWar/7089034
 */
export const FIRST_BUILD_NUMBER_2026_2 = 238483

/**
 * The build in which the trunk counter was really bumped for 2026.3:
 * https://teamcity-nightly.labs.intellij.net/buildConfiguration/TC_Trunk_BuildDistTarGzWar/7090301
 */
export const FIRST_BUILD_NUMBER_2026_3 = 244545

/**
 * Before 2026.3, accounts were stamped only with a build number on creation. Starting from 2026.3
 * the display version is recorded next to it, so the major version can be read directly.
 *
 * The boundaries above let the major version be recovered from a build number for accounts created
 * earlier. Each one is the lowest build number that reliably belongs to that release. They matter
 * only while the running server is on 2026.1..2026.3, because:
 *
 *   - 2026.1 is the current official TeamCity version, which may still require this fix through a
 *     backport
 *   - 2026.3 is the last release that can create accounts without a display version, because its
 *     EAP builds predate this change
 *
 * Once the Welcome popup exceeds the 2026.3 release, that is, becomes "Welcome to 2026.4" or
 * "Welcome to 2027.1", the whole file may be deleted, because any account without a display
 * version was created on 2026.3 at the latest and is therefore "old", thus eligible for the
 * Welcome popup.
 */
export function resolveMajorVersion(
  displayVersion: string | null | undefined,
  buildNumber: string | null | undefined,
): string | null {
  const fromDisplayVersion = extractMajorVersion(displayVersion)
  if (fromDisplayVersion !== 'unknown') {
    return fromDisplayVersion
  }

  if (!buildNumber) {
    // Super user or guest user
    return null
  }

  const parsedBuildNumber = Number(buildNumber)
  if (!Number.isFinite(parsedBuildNumber)) {
    return null
  }

  if (parsedBuildNumber < FIRST_BUILD_NUMBER_2026_1) {
    // Anything older makes no difference: callers only ask whether this differs from the version
    // running now
    return '2025.11'
  } else if (parsedBuildNumber < FIRST_BUILD_NUMBER_2026_2) {
    return '2026.1'
  } else if (parsedBuildNumber < FIRST_BUILD_NUMBER_2026_3) {
    return '2026.2'
  } else {
    return '2026.3'
  }
}
